// src/components/settings/SettingsTab.jsx
// 설정 탭: 객실 관리 + 메시지 템플릿 편집

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useApp } from '../../context/AppContext';
import { DEFAULT_TEMPLATE_STRINGS, renderTemplate } from '../../services/messageTemplates';
import { firebaseSendPasswordReset, fsGetAdminParsingReport } from '../../services/firebase';

const ADMIN_EMAIL = 'whddn4239@naver.com';
import { generateTemplateFromExisting } from '../../services/claudeApi';

// ─── 상수 ───

// 객실별 템플릿 (객실마다 다를 수 있음)
const ROOM_TEMPLATE_TYPES = [
  { id: 'checkin',    label: '체크인',    icon: '🏠' },
  { id: 'mannertime', label: '매너타임',  icon: '🔔' },
  { id: 'checkout',   label: '체크아웃',  icon: '👋' },
];

// 공통 템플릿 (숙소 전체 공유)
const COMMON_TEMPLATE_TYPES = [
  { id: 'reservation', label: '예약확인', icon: '📋' },
  { id: 'payment',     label: '입금확인', icon: '💰' },
  { id: 'review',      label: '리뷰요청', icon: '⭐' },
];

const VARIABLES = [
  { key: '{이름}',    desc: '예약자 이름' },
  { key: '{객실명}',  desc: '객실 이름' },
  { key: '{입실시간}', desc: '체크인 시간' },
  { key: '{퇴실시간}', desc: '퇴실 시간' },
  { key: '{안내사항}', desc: '객실 안내사항' },
  { key: '{체크인날짜}', desc: '체크인 날짜' },
  { key: '{숙소명}',  desc: '숙소 이름' },
  { key: '{문의전화}', desc: '문의 전화번호' },
];

const EMPTY_ROOM_FORM = {
  name: '',
  capacity: '',
  checkinTime: '15:00',
  checkoutTime: '11:00',
  notes: '',
};

// ═══════════════════════════════════════
// 객실 추가/편집 모달
// ═══════════════════════════════════════
function RoomEditor({ room, onClose, onOpenTemplate }) {
  const { addRoom, updateRoom, showToast } = useApp();
  const isNew = !room;

  const initialForm = room
    ? { name: room.name, capacity: String(room.capacity), checkinTime: room.checkinTime, checkoutTime: room.checkoutTime, notes: room.notes || '' }
    : { ...EMPTY_ROOM_FORM };

  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState('');

  // 편집 모드에서 변경 여부 (신규는 이름만 있으면 활성)
  const isModified = isNew
    ? !!form.name.trim()
    : Object.keys(form).some(k => form[k] !== initialForm[k]);

  function update(key, value) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  // 현재 폼 값으로 체크인 메시지 미리보기
  const previewRoom = {
    name: form.name || '객실명',
    checkinTime: form.checkinTime || '15:00',
    checkoutTime: form.checkoutTime || '11:00',
    notes: form.notes,
  };
  const previewText = renderTemplate(DEFAULT_TEMPLATE_STRINGS.checkin, previewRoom, { name: '홍길동' });

  async function handleSave() {
    if (!form.name.trim()) {
      setError('객실명을 입력해주세요');
      return;
    }
    const data = {
      name: form.name.trim(),
      capacity: parseInt(form.capacity) || 4,
      checkinTime: form.checkinTime || '15:00',
      checkoutTime: form.checkoutTime || '11:00',
      notes: form.notes.trim(),
    };
    if (isNew) {
      const newId = await addRoom(data);
      showToast(`[${data.name}] 객실이 추가됐습니다 ✓`);
      // 저장 후 체크인 템플릿 편집으로 자동 전환
      if (onOpenTemplate && newId) {
        onOpenTemplate({ id: newId, ...data });
      } else {
        onClose();
      }
    } else {
      updateRoom({ id: room.id, ...data });
      showToast(`[${data.name}] 객실 정보가 저장됐습니다`);
      onClose();
    }
  }

  // 하단 버튼 영역 높이
  const FOOTER_H = isNew ? 86 : 68;

  return (
    <>
      {/* 딤 배경 */}
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.45)' }} />

      {/* 모달 — 화면 중앙 배치 */}
      <div style={{
        position: 'fixed',
        top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 'calc(100% - 32px)',
        maxWidth: 440,
        maxHeight: '88vh',
        zIndex: 201,
        background: 'white',
        borderRadius: 16,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 8px 40px rgba(0,0,0,0.22)',
      }}>
        {/* 상단 여백 대체용 빈 div */}
        <div style={{ flexShrink: 0, height: 4 }} />

        {/* 헤더 */}
        <div style={{ flexShrink: 0 }}>
          <SheetHeader
            title={isNew ? '객실 추가' : '객실 편집'}
            subtitle={isNew ? '정보를 입력하면 체크인 메시지가 자동 설정됩니다' : room.name}
            onClose={onClose}
          />
        </div>

        {/* 폼 — 이 영역만 스크롤 */}
        <div style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          padding: '0 16px 8px',
        }}>
          {error && (
            <div style={{ background: 'var(--red-light)', color: '#991b1b', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 8 }}>
              {error}
            </div>
          )}

          {/* 객실명 */}
          <div style={{ marginBottom: 8 }}>
            <label className="input-label">객실명 *</label>
            <input
              className="input"
              placeholder="예: 힐링독채 A, 단체동 B"
              value={form.name}
              onChange={e => { update('name', e.target.value); setError(''); }}
            />
          </div>

          {/* 인원·시간 */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
            <div>
              <label className="input-label">최대 인원</label>
              <input className="input" type="number" min="1" placeholder="4"
                value={form.capacity} onChange={e => update('capacity', e.target.value)} />
            </div>
            <div>
              <label className="input-label">체크인</label>
              <input className="input" placeholder="15:00"
                value={form.checkinTime} onChange={e => update('checkinTime', e.target.value)} />
            </div>
            <div>
              <label className="input-label">퇴실</label>
              <input className="input" placeholder="11:00"
                value={form.checkoutTime} onChange={e => update('checkoutTime', e.target.value)} />
            </div>
          </div>

          {/* 안내사항 */}
          <div style={{ marginBottom: 6 }}>
            <label className="input-label">고객에게 전달할 추가 안내</label>
            <textarea
              className="input"
              placeholder={`예:\n- 와이파이: KT_1234 / 비밀번호 5678\n- 주차: 객실 앞 주차 가능\n- 바베큐: 2시간 전 예약 필요`}
              value={form.notes}
              onChange={e => update('notes', e.target.value)}
              style={{ minHeight: 72, resize: 'none', lineHeight: 1.6 }}
            />
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              입력한 내용이 아래 메시지에 이렇게 들어갑니다 👇
            </span>
          </div>

          {/* 미리보기 */}
          <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', margin: '4px 0 4px' }}>
            💬 고객에게 이렇게 보내집니다
          </p>
          <div className="preview-bubble" style={{ fontSize: 13, marginBottom: 4 }}>
            {previewText}
          </div>
        </div>

        {/* 버튼 — 항상 하단 고정 */}
        <div style={{
          flexShrink: 0,
          borderTop: '1px solid var(--border-light)',
          background: 'white',
          padding: '8px 16px',
          paddingBottom: 'calc(8px + env(safe-area-inset-bottom))',
        }}>
          {isNew && (
            <p style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', margin: '0 0 6px' }}>
              저장 후 체크인 메시지를 바로 확인·수정할 수 있습니다
            </p>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-outline" style={{ flex: 1, minHeight: 48, fontSize: 14 }} onClick={onClose}>
              취소
            </button>
            <button
              className="btn btn-green"
              style={{ flex: 2, minHeight: 48, fontSize: 15, fontWeight: 700, opacity: isModified ? 1 : 0.45 }}
              onClick={handleSave}
              disabled={!isModified}
            >
              {isNew ? '저장하고 메시지 확인 →' : '변경 저장'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ═══════════════════════════════════════
// 메시지 템플릿 편집 바텀시트
// ═══════════════════════════════════════
function RoomTemplateEditor({ room, onClose }) {
  const { state, setRoomTemplate, resetRoomTemplate, showToast } = useApp();
  const [activeType, setActiveType] = useState('checkin');
  const [showWizard, setShowWizard] = useState(false);
  const textareaRef = useRef(null);

  function getStored(type) {
    return state.roomTemplates?.[room.id]?.[type] ?? null;
  }

  const [drafts, setDrafts] = useState({
    checkin:    getStored('checkin')    ?? DEFAULT_TEMPLATE_STRINGS.checkin,
    mannertime: getStored('mannertime') ?? DEFAULT_TEMPLATE_STRINGS.mannertime,
    checkout:   getStored('checkout')   ?? DEFAULT_TEMPLATE_STRINGS.checkout,
  });

  useEffect(() => {
    setDrafts({
      checkin:    getStored('checkin')    ?? DEFAULT_TEMPLATE_STRINGS.checkin,
      mannertime: getStored('mannertime') ?? DEFAULT_TEMPLATE_STRINGS.mannertime,
      checkout:   getStored('checkout')   ?? DEFAULT_TEMPLATE_STRINGS.checkout,
    });
  }, [state.roomTemplates]); // eslint-disable-line

  const draft = drafts[activeType];
  const isModified = draft !== (getStored(activeType) ?? DEFAULT_TEMPLATE_STRINGS[activeType]);
  // 탭 전체 기준 — 어느 하나라도 수정됐으면 이탈 경고
  const anyModified = Object.keys(drafts).some(
    type => drafts[type] !== (getStored(type) ?? DEFAULT_TEMPLATE_STRINGS[type])
  );
  const hasCustom = getStored(activeType) !== null;
  const preview = renderTemplate(draft, room, { name: '홍길동' });

  function setDraft(text) {
    setDrafts(prev => ({ ...prev, [activeType]: text }));
  }

  function handleClose() {
    if (anyModified && !window.confirm('저장하지 않고 나가시겠습니까?')) return;
    onClose();
  }

  function insertVariable(varKey) {
    const el = textareaRef.current;
    if (!el) { setDraft(draft + varKey); return; }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    setDraft(draft.slice(0, start) + varKey + draft.slice(end));
    setTimeout(() => {
      el.selectionStart = el.selectionEnd = start + varKey.length;
      el.focus();
    }, 0);
  }

  function handleSave() {
    setRoomTemplate(room.id, activeType, draft);
    showToast('템플릿이 저장되었습니다');
  }

  function handleReset() {
    resetRoomTemplate(room.id, activeType);
    setDraft(DEFAULT_TEMPLATE_STRINGS[activeType]);
    showToast('기본값으로 초기화했습니다');
  }

  return (
    <>
    <BottomSheet onClose={handleClose}>
      <SheetHeader
        title="메시지 템플릿 편집"
        subtitle={`${room.name} · 입실 ${room.checkinTime} · 퇴실 ${room.checkoutTime}`}
        onClose={handleClose}
      />

      {/* 스크롤 영역 — minHeight:0 필수 */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px' }}>
        {/* 타입 탭 */}
        <div className="mode-toggle" style={{ marginBottom: 16 }}>
          {ROOM_TEMPLATE_TYPES.map(t => (
            <button
              key={t.id}
              className={`mode-btn ${activeType === t.id ? 'active' : ''}`}
              onClick={() => setActiveType(t.id)}
            >
              {t.icon} {t.label}
              {state.roomTemplates?.[room.id]?.[t.id] && (
                <span style={{ marginLeft: 4, fontSize: 9, color: 'var(--green)', fontWeight: 700 }}>커스텀</span>
              )}
            </button>
          ))}
        </div>

        {/* 편집 영역 */}
        <div style={{ position: 'relative', marginBottom: 10 }}>
          <textarea
            ref={textareaRef}
            className="input"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            style={{
              minHeight: 160, resize: 'none', lineHeight: 1.7,
              fontSize: 14, fontFamily: 'inherit',
              borderColor: isModified ? 'var(--green)' : 'var(--border)',
              paddingBottom: 28,
            }}
          />
          {isModified && (
            <span style={{ position: 'absolute', bottom: 10, right: 12, fontSize: 10, color: 'var(--green)', fontWeight: 600 }}>
              수정됨
            </span>
          )}
        </div>

        {/* 변수 칩 */}
        <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '0 0 6px', fontWeight: 600 }}>
          변수 삽입 — 탭하면 커서 위치에 추가
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
          {VARIABLES.map(v => (
            <button
              key={v.key}
              onClick={() => insertVariable(v.key)}
              title={v.desc}
              style={{
                padding: '5px 10px', border: '1.5px solid var(--green-border)',
                borderRadius: 20, background: 'var(--green-light)',
                color: 'var(--green-dark)', fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}
            >
              {v.key}
            </button>
          ))}
        </div>

        {/* AI 템플릿 만들기 버튼 */}
        <button
          onClick={() => setShowWizard(true)}
          style={{
            width: '100%',
            background: 'var(--green-light)',
            border: '1.5px dashed var(--green-border)',
            borderRadius: 10,
            padding: '11px',
            fontSize: 13, fontWeight: 700,
            color: 'var(--green-dark)',
            cursor: 'pointer',
            marginBottom: 16,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
        >
          ✨ 기존 문구로 템플릿 만들기
        </button>

        {/* 미리보기 */}
        <p className="section-title" style={{ marginBottom: 8 }}>미리보기</p>
        <div className="preview-bubble" style={{ marginBottom: 16, fontSize: 13 }}>
          {preview}
        </div>

      </div>

      {/* 하단 고정 버튼 */}
      <div style={{ flexShrink: 0, padding: '10px 16px 20px', borderTop: '1px solid var(--border-light)', background: 'white' }}>
        {hasCustom && (
          <button
            className="btn btn-outline"
            style={{ width: '100%', padding: '9px', fontSize: 12, marginBottom: 8, color: 'var(--text-muted)' }}
            onClick={handleReset}
          >
            기본값으로 초기화
          </button>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn btn-outline"
            style={{ flex: 1, padding: '12px', fontSize: 14 }}
            onClick={handleClose}
          >
            취소
          </button>
          <button
            className="btn btn-green"
            style={{ flex: 2, padding: '12px', fontSize: 14, fontWeight: 700, opacity: isModified ? 1 : 0.45 }}
            onClick={handleSave}
            disabled={!isModified}
          >
            저장하기
          </button>
        </div>
      </div>
    </BottomSheet>

    {showWizard && (
      <AITemplateWizard
        templateType={activeType}
        onApply={(text) => {
          setDraft(text);
          showToast('AI 템플릿이 적용되었습니다. 검토 후 저장하세요.');
        }}
        onClose={() => setShowWizard(false)}
      />
    )}
    </>
  );
}

// ═══════════════════════════════════════
// AI 템플릿 생성 마법사 모달
// ═══════════════════════════════════════
function AITemplateWizard({ templateType, onApply, onClose }) {
  const [input, setInput] = useState('');
  const [tone, setTone] = useState('friendly'); // 'short' | 'friendly'
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');
  const [error, setError] = useState('');

  const typeLabel = {
    checkin: '체크인 안내',
    checkout: '퇴실 안내',
    reservation: '예약 확인',
    payment: '입금 확인',
    mannertime: '매너타임 안내',
    review: '리뷰 요청',
  }[templateType] || '안내';

  async function handleGenerate() {
    if (!input.trim()) { setError('문자 내용을 붙여넣어 주세요.'); return; }
    setLoading(true);
    setError('');
    setResult('');
    try {
      const text = await generateTemplateFromExisting({ originalText: input, tone, templateType });
      setResult(text);
    } catch (e) {
      if (e.message === 'NO_API_KEY') {
        setError('AI 기능을 사용하려면 Claude API 키가 필요합니다.');
      } else {
        setError('변환에 실패했습니다. 잠시 후 다시 시도해주세요.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      {/* 딤 */}
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.5)' }}
      />
      {/* 모달 */}
      <div
        style={{
          position: 'fixed',
          top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'calc(100% - 32px)',
          maxWidth: 440,
          maxHeight: '88dvh',
          zIndex: 301,
          background: 'white',
          borderRadius: 18,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 12px 48px rgba(0,0,0,0.22)',
          overflow: 'hidden',
        }}
      >
        {/* 헤더 */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 16px 12px',
          borderBottom: '1px solid var(--border-light)',
          flexShrink: 0,
        }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>✨ 기존 문구로 템플릿 만들기</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{typeLabel} 템플릿 변환</div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 22, color: 'var(--text-muted)', cursor: 'pointer', padding: '0 4px' }}>×</button>
        </div>

        {/* 스크롤 영역 */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>

          {/* 입력 안내 */}
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 8px', fontWeight: 600 }}>
            지금 쓰고 있는 {typeLabel} 문구를 붙여넣어 주세요
          </p>
          <textarea
            className="input"
            value={input}
            onChange={e => { setInput(e.target.value); setError(''); }}
            placeholder={`예:\n안녕하세요~ 오늘 입실하실 고객님!\n체크인은 오후 3시부터 가능합니다.\n주차는 건물 앞에 하시면 돼요.\n도착하시면 연락 주세요 😊`}
            style={{ minHeight: 130, resize: 'none', fontSize: 13, lineHeight: 1.7, marginBottom: 12 }}
          />

          {/* 톤 선택 */}
          <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', margin: '0 0 8px' }}>정리 스타일</p>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {[
              { id: 'friendly', label: '😊 친절하게', desc: '말투 유지, 자연스럽게' },
              { id: 'short',    label: '⚡ 짧게',     desc: '핵심만 간결하게' },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => setTone(t.id)}
                style={{
                  flex: 1, padding: '10px 8px',
                  border: `2px solid ${tone === t.id ? 'var(--green)' : 'var(--border)'}`,
                  borderRadius: 10,
                  background: tone === t.id ? 'var(--green-light)' : 'white',
                  cursor: 'pointer', textAlign: 'center',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 700, color: tone === t.id ? 'var(--green-dark)' : 'var(--text-primary)' }}>{t.label}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{t.desc}</div>
              </button>
            ))}
          </div>

          {/* 에러 */}
          {error && (
            <div style={{ background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#991b1b', marginBottom: 12 }}>
              {error}
            </div>
          )}

          {/* AI 변환 버튼 */}
          <button
            onClick={handleGenerate}
            disabled={loading || !input.trim()}
            style={{
              width: '100%',
              background: loading || !input.trim()
                ? 'var(--border-light)'
                : 'linear-gradient(135deg, #15803d 0%, #22c55e 100%)',
              border: 'none', borderRadius: 12,
              padding: '13px', fontSize: 14, fontWeight: 700,
              color: loading || !input.trim() ? 'var(--text-muted)' : 'white',
              cursor: loading || !input.trim() ? 'default' : 'pointer',
              marginBottom: 16,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            {loading ? (
              <><div className="spinner" style={{ borderColor: 'var(--green)', borderTopColor: 'transparent', width: 16, height: 16 }} /> AI가 변환 중입니다...</>
            ) : '✨ AI로 템플릿 변환하기'}
          </button>

          {/* 결과 미리보기 */}
          {result && (
            <div style={{ marginBottom: 4 }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--green-dark)', margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 4 }}>
                <span>✅</span> 변환 결과
              </p>
              <div style={{
                background: '#f0fdf4', border: '1.5px solid var(--green-border)',
                borderRadius: 12, padding: '14px',
                fontSize: 13, lineHeight: 1.7,
                color: 'var(--text-primary)',
                whiteSpace: 'pre-wrap', marginBottom: 12,
              }}>
                {result}
              </div>
              <button
                onClick={() => { onApply(result); onClose(); }}
                style={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #15803d 0%, #22c55e 100%)',
                  border: 'none', borderRadius: 12,
                  padding: '14px', fontSize: 15, fontWeight: 800,
                  color: 'white', cursor: 'pointer',
                  boxShadow: '0 4px 14px rgba(22,163,74,0.35)',
                }}
              >
                이 템플릿으로 저장
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ═══════════════════════════════════════
// 공통 메시지 템플릿 편집 바텀시트
// ═══════════════════════════════════════
function CommonTemplateEditor({ onClose }) {
  const { state, setCommonTemplate, resetCommonTemplate, user, showToast } = useApp();
  const [activeType, setActiveType] = useState('reservation');
  const [showWizard, setShowWizard] = useState(false);
  const textareaRef = useRef(null);

  function getStored(type) {
    return state.commonTemplates?.[type] ?? null;
  }

  const [drafts, setDrafts] = useState({
    reservation: getStored('reservation') ?? DEFAULT_TEMPLATE_STRINGS.reservation,
    payment:     getStored('payment')     ?? DEFAULT_TEMPLATE_STRINGS.payment,
    review:      getStored('review')      ?? DEFAULT_TEMPLATE_STRINGS.review,
  });

  useEffect(() => {
    setDrafts({
      reservation: getStored('reservation') ?? DEFAULT_TEMPLATE_STRINGS.reservation,
      payment:     getStored('payment')     ?? DEFAULT_TEMPLATE_STRINGS.payment,
      review:      getStored('review')      ?? DEFAULT_TEMPLATE_STRINGS.review,
    });
  }, [state.commonTemplates]); // eslint-disable-line

  const draft = drafts[activeType];
  const isModified = draft !== (getStored(activeType) ?? DEFAULT_TEMPLATE_STRINGS[activeType]);
  const anyModified = Object.keys(drafts).some(
    type => drafts[type] !== (getStored(type) ?? DEFAULT_TEMPLATE_STRINGS[type])
  );
  const hasCustom = getStored(activeType) !== null;

  const previewData = { name: '홍길동', pensionName: user?.pensionName || '저희 숙소', pensionPhone: user?.phone || '문의 전화' };
  const preview = renderTemplate(draft, {}, previewData);

  function setDraft(text) {
    setDrafts(prev => ({ ...prev, [activeType]: text }));
  }

  function handleClose() {
    if (anyModified && !window.confirm('저장하지 않고 나가시겠습니까?')) return;
    onClose();
  }

  function insertVariable(varKey) {
    const el = textareaRef.current;
    if (!el) { setDraft(draft + varKey); return; }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    setDraft(draft.slice(0, start) + varKey + draft.slice(end));
    setTimeout(() => {
      el.selectionStart = el.selectionEnd = start + varKey.length;
      el.focus();
    }, 0);
  }

  function handleSave() {
    setCommonTemplate(activeType, draft);
    showToast('템플릿이 저장되었습니다');
  }

  function handleReset() {
    resetCommonTemplate(activeType);
    setDraft(DEFAULT_TEMPLATE_STRINGS[activeType]);
    showToast('기본값으로 초기화했습니다');
  }

  return (
    <>
    <BottomSheet onClose={handleClose}>
      <SheetHeader
        title="공통 메시지 템플릿"
        subtitle="숙소 전체에 공유되는 메시지 템플릿"
        onClose={handleClose}
      />

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px' }}>
        {/* 타입 탭 */}
        <div className="mode-toggle" style={{ marginBottom: 16 }}>
          {COMMON_TEMPLATE_TYPES.map(t => (
            <button
              key={t.id}
              className={`mode-btn ${activeType === t.id ? 'active' : ''}`}
              onClick={() => setActiveType(t.id)}
            >
              {t.icon} {t.label}
              {state.commonTemplates?.[t.id] && (
                <span style={{ marginLeft: 4, fontSize: 9, color: 'var(--green)', fontWeight: 700 }}>커스텀</span>
              )}
            </button>
          ))}
        </div>

        {/* 편집 영역 */}
        <div style={{ position: 'relative', marginBottom: 10 }}>
          <textarea
            ref={textareaRef}
            className="input"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            style={{
              minHeight: 160, resize: 'none', lineHeight: 1.7,
              fontSize: 14, fontFamily: 'inherit',
              borderColor: isModified ? 'var(--green)' : 'var(--border)',
              paddingBottom: 28,
            }}
          />
          {isModified && (
            <span style={{ position: 'absolute', bottom: 10, right: 12, fontSize: 10, color: 'var(--green)', fontWeight: 600 }}>
              수정됨
            </span>
          )}
        </div>

        {/* 변수 칩 */}
        <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '0 0 6px', fontWeight: 600 }}>
          변수 삽입 — 탭하면 커서 위치에 추가
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
          {VARIABLES.map(v => (
            <button
              key={v.key}
              onClick={() => insertVariable(v.key)}
              title={v.desc}
              style={{
                padding: '5px 10px', border: '1.5px solid var(--green-border)',
                borderRadius: 20, background: 'var(--green-light)',
                color: 'var(--green-dark)', fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}
            >
              {v.key}
            </button>
          ))}
        </div>

        {/* AI 템플릿 만들기 버튼 */}
        <button
          onClick={() => setShowWizard(true)}
          style={{
            width: '100%',
            background: 'var(--green-light)',
            border: '1.5px dashed var(--green-border)',
            borderRadius: 10,
            padding: '11px',
            fontSize: 13, fontWeight: 700,
            color: 'var(--green-dark)',
            cursor: 'pointer',
            marginBottom: 16,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
        >
          ✨ 기존 문구로 템플릿 만들기
        </button>

        {/* 미리보기 */}
        <p className="section-title" style={{ marginBottom: 8 }}>미리보기</p>
        <div className="preview-bubble" style={{ marginBottom: 16, fontSize: 13 }}>
          {preview}
        </div>
      </div>

      {/* 하단 고정 버튼 */}
      <div style={{ flexShrink: 0, padding: '10px 16px 20px', borderTop: '1px solid var(--border-light)', background: 'white' }}>
        {hasCustom && (
          <button
            className="btn btn-outline"
            style={{ width: '100%', padding: '9px', fontSize: 12, marginBottom: 8, color: 'var(--text-muted)' }}
            onClick={handleReset}
          >
            기본값으로 초기화
          </button>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn btn-outline"
            style={{ flex: 1, padding: '12px', fontSize: 14 }}
            onClick={handleClose}
          >
            취소
          </button>
          <button
            className="btn btn-green"
            style={{ flex: 2, padding: '12px', fontSize: 14, fontWeight: 700, opacity: isModified ? 1 : 0.45 }}
            onClick={handleSave}
            disabled={!isModified}
          >
            저장하기
          </button>
        </div>
      </div>
    </BottomSheet>

    {showWizard && (
      <AITemplateWizard
        templateType={activeType}
        onApply={(text) => {
          setDraft(text);
          showToast('AI 템플릿이 적용되었습니다. 검토 후 저장하세요.');
        }}
        onClose={() => setShowWizard(false)}
      />
    )}
    </>
  );
}

// ═══════════════════════════════════════
// 공용 바텀시트 래퍼
// ═══════════════════════════════════════
function BottomSheet({ onClose, children }) {
  return (
    <>
      {/* 딤 */}
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.45)' }}
      />
      {/* 모달 — 화면 중앙 */}
      <div
        style={{
          position: 'fixed',
          top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'calc(100% - 32px)',
          maxWidth: 440,
          maxHeight: '88dvh',
          zIndex: 201,
          background: 'white',
          borderRadius: 16,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 8px 40px rgba(0,0,0,0.22)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {children}
      </div>
    </>
  );
}

function SheetHeader({ title, subtitle, onClose }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '14px 16px 12px',
      borderBottom: '1px solid var(--border-light)',
      flexShrink: 0,
    }}>
      <div>
        <div style={{ fontWeight: 700, fontSize: 16 }}>{title}</div>
        {subtitle && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{subtitle}</div>}
      </div>
      <button
        onClick={onClose}
        style={{ border: 'none', background: 'none', fontSize: 22, color: 'var(--text-muted)', cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}
      >
        ×
      </button>
    </div>
  );
}

// ═══════════════════════════════════════
// 객실 카드 (관리 섹션용)
// ═══════════════════════════════════════
function RoomCard({ room, onEdit, onEditTemplate }) {
  const { deleteRoom, showToast } = useApp();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  async function handleDelete() {
    if (!room.id) {
      showToast('객실 ID가 없습니다. 다시 시도해주세요.', 'error');
      return;
    }
    setDeleting(true);
    try {
      await deleteRoom(room.id);
      // onRoomsChange가 상태를 갱신해 카드가 자동으로 사라짐
      showToast(`[${room.name}] 객실이 삭제되었습니다`);
    } catch (e) {
      console.error('[deleteRoom] 실패 코드:', e?.code, '메시지:', e?.message);
      const msg = e?.code === 'permission-denied'
        ? '권한이 없습니다. Firebase 보안 규칙을 확인하세요.'
        : `삭제 실패: ${e?.message || '알 수 없는 오류'}`;
      showToast(msg, 'error');
      setDeleting(false);
      setConfirmDelete(false);
    }
  }

  if (confirmDelete) {
    return (
      <div className="card" style={{ border: '1.5px solid var(--red)', background: '#fff5f5' }}>
        <p style={{ margin: '0 0 12px', fontWeight: 700, fontSize: 14, color: '#991b1b' }}>
          [{room.name}]을 삭제할까요?
        </p>
        <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--text-secondary)' }}>
          객실과 메시지 템플릿이 함께 삭제됩니다. 예약 이력은 유지됩니다.
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline" style={{ flex: 1, padding: '10px', fontSize: 13 }} onClick={() => setConfirmDelete(false)}>
            취소
          </button>
          <button
            className="btn btn-danger"
            style={{ flex: 1, padding: '10px', fontSize: 13, opacity: deleting ? 0.6 : 1 }}
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? '삭제 중…' : '삭제'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
      {/* 객실 정보 */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 3 }}>{room.name}</div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
          입실 {room.checkinTime} · 퇴실 {room.checkoutTime} · 최대 {room.capacity}명
        </div>
        {room.notes ? (
          <div style={{
            fontSize: 12, color: 'var(--text-muted)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            maxWidth: '100%',
          }}>
            {room.notes}
          </div>
        ) : (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>안내사항 없음</div>
        )}
      </div>

      {/* 액션 버튼 */}
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        <button
          onClick={onEdit}
          style={{
            padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border)',
            background: 'white', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            color: 'var(--text-secondary)',
          }}
        >
          편집
        </button>
        <button
          onClick={() => setConfirmDelete(true)}
          style={{
            padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)',
            background: 'white', fontSize: 12, cursor: 'pointer',
            color: 'var(--text-muted)',
          }}
          title="삭제"
        >
          🗑
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════
// 메인 설정 탭
// ═══════════════════════════════════════
export default function SettingsTab() {
  const { state, logout, showToast } = useApp();
  const { rooms, roomTemplates, commonTemplates } = state;
  const user = state.user;

  // 패널 상태: null | { mode: 'room', room } | { mode: 'template', room }
  const [panel, setPanel] = useState(null);
  const [pwResetSent, setPwResetSent] = useState(false);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  const isAdmin = user?.email === ADMIN_EMAIL;

  const loadReport = useCallback(async () => {
    if (!isAdmin) return;
    setReportLoading(true);
    try {
      const data = await fsGetAdminParsingReport();
      setReport(data);
    } catch {
      // collectionGroup 인덱스 미생성 등 에러 무시
    } finally {
      setReportLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { loadReport(); }, [loadReport]);

  async function handlePasswordReset() {
    if (!user?.email) return;
    try {
      await firebaseSendPasswordReset(user.email);
      setPwResetSent(true);
      showToast('비밀번호 재설정 메일을 보냈습니다');
    } catch {
      showToast('메일 발송에 실패했습니다. 다시 시도해주세요', 'error');
    }
  }

  async function handleLogout() {
    if (!window.confirm('로그아웃 하시겠습니까?')) return;
    await logout();
  }

  const openAddRoom      = ()     => setPanel({ mode: 'room', room: null });
  const openEditRoom     = (room) => setPanel({ mode: 'room', room });
  const openTemplate     = (room) => setPanel({ mode: 'template', room });
  const openCommon       = ()     => setPanel({ mode: 'common' });
  const closePanel       = ()     => setPanel(null);

  const customCount = Object.keys(roomTemplates).length;
  const commonCustomCount = Object.keys(state.commonTemplates || {}).length;

  return (
    <div className="tab-content">

      {/* ══════════════════════════════
          섹션 1: 객실 관리
      ══════════════════════════════ */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <p className="section-title" style={{ margin: 0 }}>객실 관리</p>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{rooms.length}개</span>
      </div>

      {rooms.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '28px 16px' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🏠</div>
          <p style={{ margin: '0 0 4px', fontWeight: 600, color: 'var(--text-secondary)' }}>등록된 객실이 없습니다</p>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>아래 버튼으로 첫 객실을 추가하세요</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rooms.map(room => (
            <RoomCard
              key={room.id}
              room={room}
              onEdit={() => openEditRoom(room)}
              onEditTemplate={() => openTemplate(room)}
            />
          ))}
        </div>
      )}

      {/* 객실 추가 버튼 */}
      <button
        className="btn btn-outline btn-block"
        style={{ marginTop: 12, padding: '13px', fontSize: 14 }}
        onClick={openAddRoom}
      >
        + 객실 추가
      </button>

      {/* ══════════════════════════════
          섹션 2: 메시지 템플릿
      ══════════════════════════════ */}
      <p className="section-title" style={{ margin: '24px 0 10px' }}>메시지 템플릿</p>

      {/* ── 공통 템플릿 (예약확인·입금확인·리뷰요청) ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)' }}>공통 템플릿</span>
        {commonCustomCount > 0 && (
          <span style={{ fontSize: 11, color: 'var(--green)', fontWeight: 600 }}>{commonCustomCount}개 커스텀</span>
        )}
      </div>
      <div
        className="card"
        style={{
          cursor: 'pointer',
          border: commonCustomCount > 0 ? '1.5px solid var(--green-border)' : '1px solid var(--border)',
          marginBottom: 20,
        }}
        onClick={openCommon}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>숙소 공통 메시지</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {COMMON_TEMPLATE_TYPES.map(t => (
                <TemplateBadge key={t.id} label={t.label} custom={!!commonTemplates?.[t.id]} />
              ))}
            </div>
          </div>
          <span style={{ fontSize: 18, color: 'var(--text-muted)' }}>›</span>
        </div>
      </div>

      {/* ── 객실별 템플릿 (체크인·매너타임·체크아웃) ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)' }}>객실별 템플릿</span>
        {customCount > 0 && (
          <span style={{ fontSize: 11, color: 'var(--green)', fontWeight: 600 }}>{customCount}개 커스텀</span>
        )}
      </div>

      {rooms.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '16px', color: 'var(--text-muted)', fontSize: 13 }}>
          객실을 먼저 추가해주세요
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rooms.map(room => {
            const tmpl = roomTemplates?.[room.id];
            const hasAny = !!(tmpl?.checkin || tmpl?.mannertime || tmpl?.checkout);
            return (
              <div
                key={room.id}
                className="card"
                style={{
                  cursor: 'pointer',
                  border: hasAny ? '1.5px solid var(--green-border)' : '1px solid var(--border)',
                }}
                onClick={() => openTemplate(room)}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>{room.name}</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <TemplateBadge label="체크인" custom={!!tmpl?.checkin} />
                      <TemplateBadge label="매너타임" custom={!!tmpl?.mannertime} />
                      <TemplateBadge label="체크아웃" custom={!!tmpl?.checkout} />
                    </div>
                  </div>
                  <span style={{ fontSize: 18, color: 'var(--text-muted)' }}>›</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ─── 변수 안내 ─── */}
      <div className="card" style={{ marginTop: 20, background: 'var(--border-light)', border: 'none' }}>
        <p style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)' }}>사용 가능한 변수</p>
        {VARIABLES.map(v => (
          <div key={v.key} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid var(--border)' }}>
            <code style={{ fontSize: 12, color: 'var(--green-dark)', background: 'var(--green-light)', padding: '1px 6px', borderRadius: 4 }}>
              {v.key}
            </code>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{v.desc}</span>
          </div>
        ))}
        <p style={{ margin: '10px 0 0', fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.6 }}>
          객실별 커스텀 템플릿이 없으면 기본 템플릿이 사용됩니다.
        </p>
      </div>

      {/* ══════════════════════════════
          섹션 3: 파싱 정확도 리포트
      ══════════════════════════════ */}
      {isAdmin && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '28px 0 10px' }}>
            <p className="section-title" style={{ margin: 0 }}>파싱 정확도 리포트</p>
            <button
              onClick={loadReport}
              disabled={reportLoading}
              style={{ fontSize: 12, color: 'var(--green-dark)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: '4px 8px' }}
            >
              {reportLoading ? '조회 중...' : '새로고침'}
            </button>
          </div>

          {reportLoading ? (
            <div className="card" style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)', fontSize: 13 }}>
              데이터를 불러오는 중...
            </div>
          ) : !report || report.total === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '24px 16px' }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>📊</div>
              <p style={{ margin: '0 0 4px', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 14 }}>아직 데이터가 없습니다</p>
              <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>예약 문자를 파싱하고 발송하면 자동으로 기록됩니다</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

              {/* 요약 카드 */}
              <div className="card" style={{ padding: '16px' }}>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12, fontWeight: 600 }}>최근 30일 요약</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                  {[
                    { label: '총 파싱', value: report.total + '건', color: 'var(--text-primary)' },
                    { label: '수정된 건수', value: report.editedCount + '건', color: '#d97706' },
                    { label: '수정률', value: report.editRate + '%', color: report.editRate >= 30 ? '#dc2626' : 'var(--green-dark)' },
                  ].map(({ label, value, color }) => (
                    <div key={label} style={{ textAlign: 'center', padding: '10px 6px', background: '#fafafa', borderRadius: 10, border: '1px solid var(--border-light)' }}>
                      <div style={{ fontSize: 18, fontWeight: 800, color }}>{value}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>{label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 필드별 수정 횟수 */}
              <div className="card" style={{ padding: '16px' }}>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12, fontWeight: 600 }}>필드별 수정 횟수</div>
                {[
                  { key: 'name', label: '이름' },
                  { key: 'phone', label: '전화번호' },
                  { key: 'checkin', label: '체크인' },
                  { key: 'checkout', label: '체크아웃' },
                  { key: 'guests', label: '인원' },
                  { key: 'room', label: '객실' },
                ].map(({ key, label }) => {
                  const count = report.fieldCounts[key] || 0;
                  const pct = report.total > 0 ? Math.round((count / report.total) * 100) : 0;
                  return (
                    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', width: 56, flexShrink: 0 }}>{label}</span>
                      <div style={{ flex: 1, height: 8, background: '#f3f4f6', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: pct >= 30 ? '#f87171' : 'var(--green)', borderRadius: 4, transition: 'width 0.3s' }} />
                      </div>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)', width: 48, textAlign: 'right', flexShrink: 0 }}>{count}회 ({pct}%)</span>
                    </div>
                  );
                })}
              </div>

              {/* 가장 많이 실패하는 필드 TOP3 */}
              {report.top3Fields.length > 0 && (
                <div className="card" style={{ padding: '16px' }}>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10, fontWeight: 600 }}>자주 틀리는 필드 TOP3</div>
                  {report.top3Fields.map(([field, count], i) => {
                    const LABELS = { name: '이름', phone: '전화번호', checkin: '체크인', checkout: '체크아웃', guests: '인원', room: '객실' };
                    return (
                      <div key={field} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: i < report.top3Fields.length - 1 ? 8 : 0 }}>
                        <span style={{ fontSize: 14, width: 20, textAlign: 'center', flexShrink: 0 }}>
                          {i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'}
                        </span>
                        <span style={{ fontSize: 14, fontWeight: 700, flex: 1 }}>{LABELS[field] || field}</span>
                        <span style={{ fontSize: 13, color: '#dc2626', fontWeight: 700 }}>{count}회</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* OTA별 수정률 + TOP3 */}
              {report.otaStats.length > 0 && (
                <div className="card" style={{ padding: '16px' }}>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12, fontWeight: 600 }}>OTA별 파싱 현황</div>
                  {report.otaStats.map(({ channel, total, edited, rate }) => (
                    <div key={channel} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', width: 68, flexShrink: 0 }}>{channel}</span>
                      <div style={{ flex: 1, height: 8, background: '#f3f4f6', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ width: `${rate}%`, height: '100%', background: rate >= 30 ? '#f87171' : 'var(--green)', borderRadius: 4 }} />
                      </div>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)', width: 64, textAlign: 'right', flexShrink: 0 }}>{edited}/{total} ({rate}%)</span>
                    </div>
                  ))}
                  {report.top3OTAs.length > 0 && (
                    <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--border-light)' }}>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6, fontWeight: 600 }}>수정 빈도 높은 OTA TOP3</div>
                      {report.top3OTAs.map(({ channel, rate }, i) => (
                        <span key={channel} style={{ fontSize: 12, marginRight: 8 }}>
                          {i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'} {channel} ({rate}%)
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* 마지막 업데이트 */}
              <div style={{ textAlign: 'right', fontSize: 11, color: 'var(--text-muted)', paddingBottom: 4 }}>
                마지막 업데이트: {report.updatedAt.toLocaleString('ko-KR', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          )}
        </>
      )}

      {/* ══════════════════════════════
          섹션 4: 계정 정보
      ══════════════════════════════ */}
      <p className="section-title" style={{ margin: '28px 0 10px' }}>계정 정보</p>
      <div className="card" style={{ padding: '0' }}>

        {/* 아이디 (읽기 전용) */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 16px',
          borderBottom: '1px solid var(--border-light)',
        }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 3 }}>아이디 (이메일)</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
              {user?.email || '—'}
            </div>
          </div>
          <span style={{
            fontSize: 10, padding: '2px 8px', borderRadius: 10,
            background: 'var(--green-light)', color: 'var(--green-dark)', fontWeight: 600,
          }}>
            로그인 중
          </span>
        </div>

        {/* 비밀번호 변경 */}
        <button
          onClick={handlePasswordReset}
          disabled={pwResetSent}
          style={{
            width: '100%', textAlign: 'left',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 16px',
            background: 'none', border: 'none',
            borderBottom: '1px solid var(--border-light)',
            cursor: pwResetSent ? 'default' : 'pointer',
          }}
        >
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: pwResetSent ? 'var(--text-muted)' : 'var(--text-primary)' }}>
              비밀번호 변경
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
              {pwResetSent ? '재설정 메일을 보냈습니다. 메일함을 확인하세요.' : '이메일로 재설정 링크를 보내드립니다'}
            </div>
          </div>
          {pwResetSent
            ? <span style={{ fontSize: 16 }}>✅</span>
            : <span style={{ fontSize: 16, color: 'var(--text-muted)' }}>›</span>
          }
        </button>

        {/* 로그아웃 */}
        <button
          onClick={handleLogout}
          style={{
            width: '100%', textAlign: 'left',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 16px',
            background: 'none', border: 'none',
            cursor: 'pointer',
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 600, color: '#dc2626' }}>로그아웃</span>
          <span style={{ fontSize: 16, color: '#dc2626', opacity: 0.5 }}>›</span>
        </button>

      </div>

      {/* ─── 패널 ─── */}
      {panel?.mode === 'room' && (
        <RoomEditor
          room={panel.room}
          onClose={closePanel}
          onOpenTemplate={panel.room === null ? openTemplate : undefined}
        />
      )}
      {panel?.mode === 'template' && (
        <RoomTemplateEditor room={panel.room} onClose={closePanel} />
      )}
      {panel?.mode === 'common' && (
        <CommonTemplateEditor onClose={closePanel} />
      )}
    </div>
  );
}

function TemplateBadge({ label, custom }) {
  return (
    <span style={{
      fontSize: 10, padding: '2px 8px', borderRadius: 20, fontWeight: 600,
      background: custom ? 'var(--green-light)' : 'var(--border-light)',
      color: custom ? 'var(--green-dark)' : 'var(--text-muted)',
      border: custom ? '1px solid var(--green-border)' : '1px solid var(--border)',
    }}>
      {label} {custom ? '커스텀' : '기본값'}
    </span>
  );
}
